import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { default as Modal } from 'antd/es/modal';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import type { InferType } from 'yup';

import { hasValue } from 'utils/hasValue';

import {
  getGetDisbursementPayoutBatchesQueryKey,
  useGetCustomerConfigurations,
  usePostDisbursementPayoutBatches
} from 'api';
import { uploadBatchDialogSchema as schema } from 'validators/uploadBatchDialog';

import { useOrganisationId } from 'hooks/context';
import { useFeatures } from 'hooks/useFeatures';
import { useGoogleAnalyticsDispatcher } from 'hooks/useGoogleAnalyticsDispatcher';

import { BlindingDots } from '@atoms/BlindingDots/BlindingDots';
import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { TertiaryButton } from '@atoms/buttons/TertiaryButton';
import { DetailsBatchAttachmentSectionContainer } from '@atoms/DetailsAttachmentSection/DetailsBatchAttachmentSectionContainer';
import { RollingLoader } from '@atoms/RollingLoader/RollingLoader';

import { InputField } from '@molecules/InputField';

import { CorrectDataHandler } from './CorrectDataHandler';
import { DownloadErrorButton } from './DownloadErrorButton';
import { ErrorHandler } from './ErrorHandler';
import { FileInputField } from './FileInputField';

import type { RefetchBatchesType } from '..';

interface Inputs extends InferType<typeof schema> {
  base: null;
  file: FileList;
}

type Props = {
  open: boolean;
  onClose: (value: boolean) => void;
  setIsCreateBatchDialogVisible: (value: boolean) => void;
  refetchBatches: RefetchBatchesType;
};

export const UploadBatchDialog = ({
  open,
  onClose,
  setIsCreateBatchDialogVisible,
  refetchBatches
}: Props) => {
  const {
    handleSubmit,
    register,
    watch,
    formState,
    reset: resetForm
  } = useForm<Inputs>({
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  // this is needed to cause re-render on onChange value
  watch('file');

  const queryClient = useQueryClient();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const {
    isError,
    mutate: uploadBatch,
    data,
    isPending: isLoading,
    error: uploadError,
    reset: resetMutation
  } = usePostDisbursementPayoutBatches({
    mutation: {
      retry: false
    }
  });

  const navigate = useNavigate();
  const organisationId = useOrganisationId<number>();

  const { data: { data: orgSettings } = {} } =
    useGetCustomerConfigurations(organisationId);

  const dispatchGAEvent = useGoogleAnalyticsDispatcher();

  const { getFeatureFlag } = useFeatures();

  if (!orgSettings) return null;

  const isBatchesPageEnabled = !!getFeatureFlag(
    'CBP-1761-customer-app-batches'
  );

  const isApprovalEnabled = Number(orgSettings.batch_number_of_approvers) > 0;

  const onSubmit = async ({
    name,
    file
  }: {
    name: string | undefined;
    file: FileList;
  }) => {
    const formData = new FormData();
    if (!!name) {
      formData.append('name', name);
    }
    formData.append('file', file[0]);

    dispatchGAEvent('create_batch_submitted');
    uploadBatch(
      {
        data: {
          file: formData.get('file') as File,
          name: (formData.get('name') as string) ?? undefined
        },
        organisationId
      },
      {
        onSuccess: () => {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => {
            refetchBatches();
            queryClient.invalidateQueries({
              queryKey: getGetDisbursementPayoutBatchesQueryKey(organisationId)
            });
          }, 3000);
        }
      }
    );
  };

  const onDialogClose = (value: boolean) => {
    resetForm();
    resetMutation();
    onClose(value);
  };

  const openCreateBatchTemplate = () => {
    onDialogClose(false);
    setIsCreateBatchDialogVisible(true);
  };

  const handleUploadAnotherBatch = () => {
    resetForm();
    resetMutation();
  };

  const submitDisabled =
    formState.isSubmitSuccessful ||
    !formState.dirtyFields.file ||
    !formState.isValid;

  return (
    <Modal
      open={open}
      onCancel={() => onDialogClose(true)}
      title="Upload batch file"
      footer={false}
    >
      {!isLoading && !data && !isError && (
        <div className="flex justify-center">
          <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <InputField
              {...register('name')}
              className="mb-4"
              label="Name (if left blank the file name will be used)"
            />
            <FileInputField {...register('file')} />

            {formState.errors.file && (
              <p className="ml-2 mt-1 text-sm text-feedback-error-strong">
                {formState.errors.file.message?.toString()}
              </p>
            )}
            <PrimaryButton
              htmlType="submit"
              size="large"
              className="mt-8 w-full"
              disabled={submitDisabled}
            >
              Upload batch
            </PrimaryButton>
            <TertiaryButton
              onClick={openCreateBatchTemplate}
              className="mt-4 w-full"
            >
              Create Batch template
            </TertiaryButton>
          </form>
        </div>
      )}
      {isLoading && (
        <div className="flex flex-col items-center gap-y-4 p-4">
          <RollingLoader ariaLabel="Uploading batch" />
          <div className="text-xs">
            <span className="invisible">...</span>
            <span>Processing</span>
            <BlindingDots />
          </div>
        </div>
      )}
      {hasValue(data?.data) && (
        <div>
          <CorrectDataHandler
            data={data}
            isApprovalEnabled={isApprovalEnabled}
            isBatchesPageEnabled={isBatchesPageEnabled}
          />

          <DetailsBatchAttachmentSectionContainer
            className="mt-6"
            resourceId={data.data!.batch_id}
          />

          <PrimaryButton
            className="mt-5 w-full"
            size="large"
            onClick={() => {
              onDialogClose(false);
              navigate(
                `/org/${organisationId}/batches/${data.data!.batch_id}?status=waiting_review`
              );
            }}
          >
            View batch
          </PrimaryButton>
          <SecondaryButton
            className="mt-4 w-full"
            onClick={handleUploadAnotherBatch}
          >
            Upload another batch
          </SecondaryButton>
        </div>
      )}
      {isError && hasValue(uploadError) && (
        <div role="alert">
          <ErrorHandler error={uploadError} />
          <DownloadErrorButton
            errorData={uploadError}
            resetForm={() => {
              resetForm();
              resetMutation();
            }}
          />
          <PrimaryButton
            type="default"
            htmlType="submit"
            size="large"
            className="mt-4 w-full"
            onClick={() => {
              resetForm();
              onDialogClose(false);
            }}
          >
            Close window
          </PrimaryButton>
        </div>
      )}
    </Modal>
  );
};
